import {
    DefaultChatTransport,
    lastAssistantMessageIsCompleteWithApprovalResponses,
    lastAssistantMessageIsCompleteWithToolCalls,
} from 'ai'
import type { FileUIPart } from 'ai'
import { computed, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePreferredDark } from '@vueuse/core'
import { generateId } from 'ai'
import { Chat } from '@ai-sdk/vue'
import { defineStore } from 'pinia'
import { useSafeLocalStorage } from '../useSafeLocalStorage'
import type {
    Chat as ChatRecord,
    AgentFileUpload,
    AgentInterface,
    AgentModel,
    AgentVoiceCapability,
    AgentGatewayOptions,
    ChatMessageActions,
    MessageFeedback,
    RevisedAnswer,
    UIChatMessage,
    AgentTools,
} from 'models'
import {
    collectDeviceContext,
    collectGeolocation,
} from 'utils/src/device-context'
import { logger } from 'utils/src/logger'
import { getTextPart } from 'utils'
import { createChatbotApiClient, type ChatbotFile } from './api'
import type { PendingAttachment } from './types'

// The store exposes `chat` (a `Chat<UIChatMessage>` computed) whose inferred
// type transitively references JSONObject. Re-export it so consumers can emit
// portable declarations that name the type (TS2742) without an unused import.
export type { JSONObject } from '@ai-sdk/provider'

/**
 * Id prefix of the spoken lines the client shows before the server has them.
 * Their own id space is what lets a reload replace them instead of duplicating
 * them (see {@link unpersistedVoiceLines}).
 */
export const VOICE_LINE_ID_PREFIX = 'voice-'

/** Text of a message, whitespace-collapsed, for comparing two transcripts. */
const spokenText = (message: UIChatMessage): string =>
    message.parts
        .filter((part) => part.type === 'text')
        .map((part) => ('text' in part ? part.text : ''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()

/**
 * The spoken lines the reload would silently drop.
 *
 * A voice line is shown the moment the provider transcribes it, while the
 * server writes it only when the turn it belongs to has been answered. So
 * between the two there is always a window, and replacing the list with the
 * persisted one wholesale made the caller's own words vanish from the screen
 * while they were still speaking (reported 2026-08-06).
 *
 * A line counts as persisted when a server message of the same role says the
 * same thing, or ENDS with it: our stream opens with the filler sentence, which
 * the provider reads out and transcribes back, so the spoken line is the
 * persisted one with a prefix. Nothing looser than that: matching on "contains"
 * either way round let a short persisted turn ("no", "sì") swallow a whole
 * question the server did not have yet, which is the disappearance this was
 * supposed to fix.
 */
const SHORTEST_MATCHABLE_LINE = 8

const unpersistedVoiceLines = (
    persisted: UIChatMessage[],
    shown: UIChatMessage[],
): UIChatMessage[] => {
    // Consumed as they match, one persisted line for one spoken line. With a
    // plain `some` a single persisted "sì" accounted for every later "sì" the
    // caller said, and the new one vanished from the screen before the server
    // had it.
    const server = persisted.map((message) => ({
        role: message.role,
        text: spokenText(message),
        taken: false,
    }))
    return shown.filter((message) => {
        if (!message.id.startsWith(VOICE_LINE_ID_PREFIX)) {
            return false
        }
        const text = spokenText(message)
        const match = server.find(
            (other) =>
                !other.taken &&
                other.role === message.role &&
                other.text.length > 0 &&
                (other.text === text ||
                    (other.text.length >= SHORTEST_MATCHABLE_LINE &&
                        text.endsWith(other.text))),
        )
        if (!match) {
            return true
        }
        match.taken = true
        return false
    })
}

type ChatbotView = 'chat' | 'conversations' | 'profile' | 'auth' | 'files'

export function useChatbotStore(agentId: string) {
    return defineStore(`chatbot-${agentId}`, () => {
        const { t, te, locale } = useI18n({ useScope: 'global' })

        // === Configuration state ===
        const baseUrl = ref('')
        const basePath = ref<string | undefined>()
        const agentIdRef = ref(agentId)
        const name = ref<string | undefined>()
        const agentType = ref<'chat' | 'reasoning' | undefined>()
        const userId = ref<string | undefined>()
        const agentInterface = ref<AgentInterface | undefined>()
        const agentModel = ref<AgentModel | undefined>()
        const agentTools = ref<AgentTools | undefined>()
        const headers = ref<Record<string, string> | undefined>()
        const revisedAnswers = ref<RevisedAnswer[] | undefined>()
        /** Compatibility with PkChatbot `model` prop — overrides agentModel.model when set */
        const model = ref<string | undefined>()
        /** Raw actions from parent prop, filtered by agentType into `actions` */
        const actionsInput = ref<ChatMessageActions[] | undefined>()
        /** File upload configuration from agent settings */
        const agentFileUpload = ref<AgentFileUpload | undefined>()
        /** Voice configuration from agent settings: absent means no voice. */
        const agentVoice = ref<AgentVoiceCapability | undefined>()
        /** Fallback models for AI Gateway routing */
        const fallbackModels = ref<string[] | undefined>()
        /** Gateway options (ZDR, provider timeouts) */
        const gatewayOptions = ref<Partial<AgentGatewayOptions> | undefined>()
        /** External context set by the host application (e.g. via setContext() on the custom element). Validated server-side against the agent's externalContextSchema. */
        const externalContext = ref<Record<string, unknown> | undefined>()
        /** System-level theme override passed by the host application */
        const systemTheme = ref<'light' | 'dark' | undefined>()
        /** Hide the synthetic welcome message built from `initialMessage` (the fullscreen layout shows a greeting in its place) */
        const hideWelcomeMessage = ref(false)

        const prefersDark = usePreferredDark()
        const isDark = computed(() => {
            let theme = agentInterface.value?.theme
            if (!theme || theme === 'auto') {
                theme = systemTheme.value
            }
            if (!theme) {
                return prefersDark.value
            }
            return theme === 'dark'
        })

        const isReasoningAgent = computed(() => agentType.value === 'reasoning')

        const actions = computed<ChatMessageActions[] | undefined>(() => {
            if (!isReasoningAgent.value) {
                return actionsInput.value
            }
            return actionsInput.value?.filter(
                (action) =>
                    !['upvote', 'downvote', 'feedback'].includes(action),
            )
        })

        // === LocalStorage chatId ===
        const storageId = useSafeLocalStorage<string | null>(
            computed(() => `${agentIdRef.value}-chatId`),
            null,
        )

        watchEffect(() => {
            if (!storageId.value) {
                storageId.value = generateId()
            }
        })

        const localChatId = computed({
            get: () => storageId.value!,
            set: (value: string) => {
                storageId.value = value
            },
        })

        // === Chat error ===
        const chatError = ref<Error | undefined>()
        const isConversationBlocked = ref(false)

        const isConversationBlockedError = computed(
            () =>
                chatError.value?.message
                    ?.toLowerCase()
                    .includes('conversation-blocked') ?? false,
        )

        function isEntityNotFoundError(error: Error): boolean {
            try {
                const parsed = JSON.parse(error.message)
                return (
                    parsed.status === 404 ||
                    parsed.type?.includes('entity-not-found')
                )
            } catch {
                return false
            }
        }

        /**
         * When an anonymous session expires and rotates, the server returns a
         * 404 (entity-not-found) because the existing chatId belongs to the
         * previous userId. In that case we transparently generate a new chatId
         * and re-send the last user message so the conversation restarts
         * without the user noticing.
         */
        function handleChatError(error: Error) {
            if (isEntityNotFoundError(error)) {
                const pendingMessages = chat.value.messages.filter(
                    (m) => m.role === 'user',
                )
                const lastUserMessage =
                    pendingMessages[pendingMessages.length - 1]
                const lastUserText = lastUserMessage
                    ? getTextPart(lastUserMessage)
                    : undefined

                localChatId.value = generateId()
                chat.value.clearError()
                chatError.value = undefined

                if (lastUserText) {
                    chat.value.sendMessage({ text: lastUserText })
                }
                return
            }

            logger.error(error)
            chatError.value = error
            if (isConversationBlockedError.value) {
                isConversationBlocked.value = true
            }
        }

        // === API client ===
        const apiClient = computed(() =>
            createChatbotApiClient({
                baseUrl: baseUrl.value,
                basePath: basePath.value,
                headers: () => headers.value,
            }),
        )

        // === Chat streaming instance ===
        const storedGeolocation = ref<{
            geolocationLatitude: number
            geolocationLongitude: number
            geolocationAccuracy?: number
            geolocationCity?: string
        } | null>(null)

        /**
         * The device this conversation is happening on, plus the position if
         * the user has shared it. Sent with every typed turn and, once, when a
         * call opens: a spoken turn reaches the backend from the voice provider
         * and can carry nothing of its own.
         */
        const buildDeviceContext = () => ({
            ...collectDeviceContext(),
            ...(storedGeolocation.value ?? {}),
        })

        const chat = computed(
            () =>
                new Chat<UIChatMessage>({
                    id: localChatId.value,
                    transport: new DefaultChatTransport({
                        api: apiClient.value.getChatEndpoint(
                            agentIdRef.value,
                            isReasoningAgent.value ? 'reasoning' : 'chat',
                        ),
                        prepareSendMessagesRequest: ({
                            messages: msgs,
                            id,
                        }) => ({
                            body: {
                                id,
                                messages: [msgs[msgs.length - 1]],
                                model: model.value,
                                prompt: agentModel.value?.prompt,
                                maxOutputTokens:
                                    agentModel.value?.maxOutputTokens,
                                temperature: agentModel.value?.temperature,
                                presencePenalty:
                                    agentModel.value?.presencePenalty,
                                frequencyPenalty:
                                    agentModel.value?.frequencyPenalty,
                                searchMode: agentModel.value?.searchMode,
                                limit: agentModel.value?.limit,
                                reasoning: agentModel.value?.reasoning,
                                webSearch: agentTools.value?.webSearch,
                                smoothStream:
                                    agentInterface.value?.smoothStream,
                                deviceContext: buildDeviceContext(),
                                externalContext: externalContext.value,
                                disabledTools: agentTools.value?.disabled,
                                fallbackModels: fallbackModels.value,
                                gatewayOptions: gatewayOptions.value,
                            },
                        }),
                        credentials: 'include',
                        // Resolved per-request: reading `headers.value` here
                        // would make this computed recreate the Chat (wiping
                        // its messages) on every auth token rotation.
                        headers: () => headers.value ?? {},
                    }),
                    // Two resume triggers: a client tool that reported its
                    // output, and a tool approval the user just answered.
                    // Without the second one the approval response would sit in
                    // the browser and the suspended call would never run.
                    sendAutomaticallyWhen: (options) =>
                        lastAssistantMessageIsCompleteWithToolCalls(options) ||
                        lastAssistantMessageIsCompleteWithApprovalResponses(
                            options,
                        ),
                    onError: handleChatError,
                    // Chat UI init keeps `onFinish` in v7 (the onEnd rename
                    // only applies to generateText/streamText callbacks).
                    onFinish: () => {
                        // A new chat is persisted on the backend only after the
                        // first message: reload the list if it's not there yet.
                        const id = localChatId.value
                        if (
                            !isReasoningAgent.value &&
                            !conversations.value.some((c) => c.id === id)
                        ) {
                            loadConversations(agentIdRef.value)
                        }
                    },
                }),
        )

        // === Messages ===
        const initialMessageResolved = computed(() => {
            const val = agentInterface.value?.initialMessage
            if (!val?.[locale.value]) {
                return te('message.defaultAgentWelcome')
                    ? t('message.defaultAgentWelcome')
                    : ''
            }
            return val[locale.value]
        })

        // Stable across recomputations (locale or config changes), so keyed
        // renders don't remount the welcome bubble and feedback stays attached.
        const welcomeMessageId = generateId()
        const welcomeMessage = computed<UIChatMessage>(() => ({
            id: welcomeMessageId,
            role: 'assistant',
            parts: [{ type: 'text', text: initialMessageResolved.value }],
        }))

        const messages = computed(() => {
            // Resolve any remaining `attachment://` URLs that haven't been
            // resolved server-side yet (e.g. in-flight streaming messages).
            const resolveInFlight = (msgs: UIChatMessage[]) =>
                msgs.map((msg) => {
                    if (msg.role !== 'user') {
                        return msg
                    }
                    const hasFileParts = msg.parts.some(
                        (p) => p.type === 'file',
                    )
                    if (!hasFileParts) {
                        return msg
                    }
                    return {
                        ...msg,
                        parts: msg.parts.map((p) => {
                            if (p.type !== 'file') {
                                return p
                            }
                            const fp = p as FileUIPart
                            if (fp.url.startsWith('attachment://')) {
                                const storedFileName = fp.url.slice(
                                    'attachment://'.length,
                                )
                                return {
                                    ...fp,
                                    url: apiClient.value.getMediaUrl(
                                        agentIdRef.value,
                                        localChatId.value,
                                        storedFileName,
                                    ),
                                } as FileUIPart
                            }
                            // Blob URLs (from pending uploads) are already
                            // usable as-is — don't append auth tokens.
                            if (fp.url.startsWith('blob:')) {
                                return fp
                            }
                            return {
                                ...fp,
                                url: apiClient.value.appendAuthToken(fp.url),
                            } as FileUIPart
                        }),
                    }
                })

            if (isReasoningAgent.value) {
                return resolveInFlight(chat.value.messages)
            }
            if (
                !hideWelcomeMessage.value &&
                chat.value.messages?.[0]?.role !== 'assistant'
            ) {
                return [
                    welcomeMessage.value,
                    ...resolveInFlight(chat.value.messages),
                ]
            }
            return resolveInFlight(chat.value.messages)
        })

        // === Localized placeholder ===
        const inputMessagePlaceholder = computed(() => {
            const val = agentInterface.value?.messagePlaceholder
            if (!val?.[locale.value]) {
                return te('placeholder.askAnything')
                    ? t('placeholder.askAnything')
                    : ''
            }
            return val[locale.value]
        })

        // === Conversations ===
        const conversations = ref<ChatRecord[]>([])
        const isLoadingConversations = ref(false)
        const chatFiles = ref<ChatbotFile[]>([])
        const isLoadingChatFiles = ref(false)

        // === Navigation ===
        const activeView = ref<ChatbotView>('chat')

        // === Message feedback state ===
        const messageFeedbacks = ref<MessageFeedback[]>([])
        const feedbackDialogMessage = ref<UIChatMessage | undefined>()
        const isFeedbackSubmitting = ref(false)
        const isFeedbackSubmitted = ref(false)
        const feedbackSubmitError = ref<string | undefined>()

        // === Lead form state ===
        const isLeadSubmitted = ref(false)
        const isLoadingSubmitLead = ref(false)
        const submitLeadError = ref<string | undefined>()

        // === Input ===
        const input = ref('')
        const pendingAttachments = ref<PendingAttachment[]>([])
        const lastSubmittedInput = ref('')
        const messageCountBeforeSubmit = ref(0)

        // === Internal loaders ===
        async function loadConversations(id: string) {
            if (isReasoningAgent.value) {
                conversations.value = []
                return
            }
            isLoadingConversations.value = true
            try {
                conversations.value = await apiClient.value.getConversations(
                    id,
                    userId.value,
                )
            } catch {
                conversations.value = []
            } finally {
                isLoadingConversations.value = false
            }
        }

        // Files uploaded in the current chat (the user's "My files" entries
        // tied to this conversation). No chat → nothing to load.
        async function loadChatFiles() {
            const chatId = localChatId.value
            if (!chatId) {
                chatFiles.value = []
                return
            }
            isLoadingChatFiles.value = true
            try {
                chatFiles.value = await apiClient.value.getChatFiles(chatId)
            } catch {
                chatFiles.value = []
            } finally {
                isLoadingChatFiles.value = false
            }
        }

        function getChatFileUrl(documentId: string, inline = false) {
            return apiClient.value.getChatFileDownloadUrl(documentId, inline)
        }

        function getChatFilePreviewUrl(documentId: string) {
            return apiClient.value.getChatFilePreviewUrl(documentId)
        }

        // Exclude a file from the user's KB (soft-delete server-side); drop it
        // from the local list on success.
        async function deleteChatFile(documentId: string) {
            await apiClient.value.deleteChatFile(documentId)
            chatFiles.value = chatFiles.value.filter(
                (file) => file.documentId !== documentId,
            )
        }

        async function loadChat(id: string, chatId: string) {
            // Reasoning chats are never persisted and the Chat instance is
            // recreated empty on every chatId/agentId change: nothing to load.
            if (isReasoningAgent.value) {
                return
            }
            try {
                const data = await apiClient.value.getChat(id, chatId)
                // The chat may have advanced while the request was in flight
                // (the user submitted a message, or the chatId rotated): never
                // clobber local messages with a stale response. A wiped list
                // mid-stream also suppresses the synthetic welcome message,
                // because the streamed reply gets re-pushed at index 0.
                if (
                    chatId !== localChatId.value ||
                    chat.value.status !== 'ready' ||
                    chat.value.messages.length > 0
                ) {
                    return
                }
                if (data) {
                    chat.value.messages = data.messages as UIChatMessage[]
                    isConversationBlocked.value = data.blocked ?? false
                }
            } catch {
                // A new chat 404s here: the instance already starts empty,
                // clearing messages would race with the first submit.
            }
        }

        /**
         * A written turn is in flight, so the message list must not be replaced
         * under it. Read through a widened local on purpose: the status changes
         * across an await, which the compiler otherwise narrows away.
         */
        const isTurnInFlight = (): boolean => {
            const status: string = chat.value.status
            return status === 'submitted' || status === 'streaming'
        }

        /** Ordering of the concurrent reloads: see `refreshMessages`. */
        let refreshSequence = 0
        let refreshApplied = 0

        /**
         * Replaces the message list with what the server persisted.
         *
         * `loadChat` deliberately refuses to touch a non-empty list, because it
         * runs on mount and must never clobber a stream in flight. A voice turn
         * is the opposite case: the answer was produced by the provider calling
         * our backend, so the persisted chat is ahead of what this client shows
         * (the transcript we appended is text only, without sources or tool
         * results). Still guarded on an idle chat, for the same reason as there.
         *
         * Returns true when the server was still BEHIND what the client shows,
         * so the caller can come back for the rest: a spoken line with no
         * persisted counterpart means the turn it belongs to has not been
         * written yet, and with it are missing the tool results and sources
         * that only the reload can bring. Without the retry those stayed
         * invisible until the page was reloaded by hand (reported 2026-08-06).
         *
         * A later reload wins over an earlier one, whatever order they answer
         * in. A call has three of these running at once (a poll while the line
         * is open, a backed-off chain after each spoken turn, one more on
         * hang-up), so out-of-order answers are the normal case: a slow fetch
         * that started first would otherwise land last and take the newest
         * messages, their tool results and their sources off the screen.
         */
        async function refreshMessages(): Promise<boolean> {
            // Only a turn in flight is a reason not to reload. Requiring
            // 'ready' also refused to reload after a failed submit ('error'),
            // which on a call left the conversation frozen on screen until the
            // page was reloaded by hand.
            if (isReasoningAgent.value || isTurnInFlight()) {
                return false
            }
            const chatId = localChatId.value
            const mine = (refreshSequence += 1)
            try {
                const data = await apiClient.value.getChat(
                    agentIdRef.value,
                    chatId,
                )
                if (
                    !data ||
                    chatId !== localChatId.value ||
                    isTurnInFlight() ||
                    mine <= refreshApplied
                ) {
                    return false
                }
                refreshApplied = mine
                const persisted = data.messages as UIChatMessage[]
                const pending = unpersistedVoiceLines(
                    persisted,
                    chat.value.messages,
                )
                chat.value.messages = [...persisted, ...pending]
                isConversationBlocked.value = data.blocked ?? false
                // Behind = a USER line the server does not have yet: that turn
                // is still being written, so its widgets are worth coming back
                // for. An agent line proves nothing: the spoken fallback
                // sentence exists only in the audio stream and never persists,
                // and counting it kept the retry chain going for the rest of
                // the call.
                return pending.some((message) => message.role === 'user')
            } catch {
                // Nothing persisted yet (a session that opened and closed
                // without a word): the local list is already the truth.
            }
            return false
        }

        /**
         * Appends a message the client already knows about, without sending
         * anything: the voice transcript, whose turn is answered server-side.
         */
        function appendMessage(message: UIChatMessage) {
            chat.value.messages = [...chat.value.messages, message]
        }

        async function loadMessageFeedbacks(chatId: string) {
            try {
                messageFeedbacks.value =
                    await apiClient.value.getMessageFeedbacks(chatId)
            } catch {
                messageFeedbacks.value = []
            }
        }

        function upsertLocalFeedback(feedback: MessageFeedback) {
            const idx = messageFeedbacks.value.findIndex(
                (f) => f.messageId === feedback.messageId,
            )
            if (idx >= 0) {
                messageFeedbacks.value[idx] = feedback
            } else {
                messageFeedbacks.value.push(feedback)
            }
        }

        function extractFeedbackContext(message: UIChatMessage) {
            const msgList = messages.value
            const idx = msgList.findIndex((m) => m.id === message.id)
            const assistantMessage = getTextPart(message) ?? ''
            const prevUser = msgList
                .slice(0, idx)
                .reverse()
                .find((m) => m.role === 'user')
            const userQuestion = getTextPart(prevUser ?? message) ?? ''
            return { userQuestion, assistantMessage }
        }

        // === Actions ===
        async function initialize(id: string, chatId: string) {
            await Promise.all([
                loadChat(id, chatId),
                loadConversations(id),
                loadMessageFeedbacks(chatId),
            ])

            if (!storedGeolocation.value) {
                collectGeolocation((lat, lon) =>
                    apiClient.value.reverseGeocode(lat, lon, locale.value),
                ).then((geo) => {
                    if (geo) {
                        storedGeolocation.value = geo
                    }
                })
            }
        }

        async function handleSubmit() {
            // Snapshot text and attachments before any async operation to avoid
            // them being cleared by startNewChat() (reasoning agents) or by a
            // concurrent store update from the component.
            const text = input.value.trim()
            const fileParts: FileUIPart[] = pendingAttachments.value
                .filter(
                    (
                        att,
                    ): att is PendingAttachment & {
                        state: 'done'
                        mediaSource: NonNullable<
                            PendingAttachment['mediaSource']
                        >
                    } => att.state === 'done' && att.mediaSource !== undefined,
                )
                .map((att) => {
                    return {
                        type: 'file' as const,
                        mediaType: att.mediaSource.mimeType,
                        url: att.localUrl,
                        filename: att.mediaSource.name,
                        providerMetadata: {
                            '8bot': {
                                media: att.mediaSource,
                            },
                        },
                    }
                })

            if (isReasoningAgent.value && messages.value.length > 0) {
                await startNewChat()
            }

            // Snapshot state before sending so we can rollback on stop
            lastSubmittedInput.value = text
            messageCountBeforeSubmit.value = chat.value.messages.length

            // Chat<UIChatMessage> can't resolve the correct sendMessage overload
            // when using a custom message generic — cast is intentional.
            const sendMessage = chat.value.sendMessage as unknown as (msg: {
                text: string
                files?: FileUIPart[]
            }) => void
            sendMessage({
                text,
                files: fileParts.length > 0 ? fileParts : undefined,
            })
            input.value = ''
            clearPendingAttachments()
        }

        /**
         * Programmatically send a user message without the input snapshot/rollback
         * logic. Logs a warning and does nothing if the chat is not ready.
         */
        function sendMessage(text: string) {
            const trimmed = text.trim()
            if (!trimmed) {
                return
            }
            if (chat.value.status !== 'ready') {
                logger.warn(
                    '[useChatbotStore] sendMessage called before chat is ready. Listen for the "ready" event on the widget element.',
                )
                return
            }
            const send = chat.value.sendMessage as unknown as (msg: {
                text: string
            }) => void
            send({ text: trimmed })
        }

        async function stopGeneration() {
            const shouldRollback = lastSubmittedInput.value.length > 0
            await chat.value.stop()
            if (shouldRollback) {
                input.value = lastSubmittedInput.value
                chat.value.messages = chat.value.messages.slice(
                    0,
                    messageCountBeforeSubmit.value,
                )
                lastSubmittedInput.value = ''
            }
        }

        // Clear rollback state when generation completes naturally
        watch(
            () => chat.value.status,
            (status) => {
                if (status === 'ready' && lastSubmittedInput.value.length > 0) {
                    lastSubmittedInput.value = ''
                }
            },
        )

        function clearPendingAttachments(revokeAll = false) {
            for (const att of pendingAttachments.value) {
                // 'done' attachment localUrls may still be referenced in
                // FileUIPart.url inside message parts — skip revocation unless
                // explicitly requested (e.g. when starting a new chat).
                if (revokeAll || att.state !== 'done') {
                    URL.revokeObjectURL(att.localUrl)
                }
            }
            pendingAttachments.value = []
            fileLimitError.value = null
        }

        async function startNewChat() {
            lastSubmittedInput.value = ''
            await stopGeneration()
            localChatId.value = generateId()
            chat.value.clearError()
            chatError.value = undefined
            isConversationBlocked.value = false
            clearPendingAttachments(true)
            navigate('chat')
            return localChatId.value
        }

        async function regenerate(
            options?: Parameters<typeof chat.value.regenerate>[0],
        ) {
            if (chat.value.status !== 'ready') {
                lastSubmittedInput.value = ''
                await stopGeneration()
            }
            await chat.value.regenerate(options)
        }

        function addToolOutput(params: {
            tool: string
            toolCallId: string
            output: unknown
        }) {
            if (params.tool === 'requestGeolocation') {
                const output = params.output as
                    | {
                          latitude: number
                          longitude: number
                          accuracy?: number
                          city?: string
                      }
                    | { error: string }
                if ('latitude' in output) {
                    storedGeolocation.value = {
                        geolocationLatitude: output.latitude,
                        geolocationLongitude: output.longitude,
                        geolocationAccuracy: output.accuracy,
                        geolocationCity: output.city,
                    }
                }
            }
            chat.value.addToolOutput(params)
        }

        /**
         * Answers a tool approval request. The response rides the same chat
         * stream: `sendAutomaticallyWhen` submits it, the backend re-validates
         * it against the persisted request and the SDK resumes (or denies) the
         * suspended call.
         */
        function addToolApprovalResponse(params: {
            id: string
            approved: boolean
            reason?: string
        }) {
            chat.value.addToolApprovalResponse(params)
        }

        // Files already committed to prior messages in this chat.
        const sentFileCount = computed(() =>
            chat.value.messages.reduce(
                (n, m) => n + m.parts.filter((p) => p.type === 'file').length,
                0,
            ),
        )

        // Single source of truth for the attachment limits (see AgentFileUpload).
        // The per-message cap counts the current draft's attachments; the
        // per-chat cap counts those plus everything already sent.
        const maxFilesPerMessage = computed(
            () => agentFileUpload.value?.maxFilesPerMessage ?? 5,
        )
        const maxFilesPerChat = computed(
            () => agentFileUpload.value?.maxFilesPerChat ?? 20,
        )
        const remainingAttachmentSlots = computed(() =>
            Math.max(
                0,
                Math.min(
                    maxFilesPerMessage.value - pendingAttachments.value.length,
                    maxFilesPerChat.value -
                        sentFileCount.value -
                        pendingAttachments.value.length,
                ),
            ),
        )
        const isAttachmentLimitReached = computed(
            () => remainingAttachmentSlots.value <= 0,
        )

        // Reactive notice surfaced to the input when a file gets rejected.
        const fileLimitError = ref<{
            reason: 'per-message' | 'per-chat'
            max: number
        } | null>(null)
        const clearFileLimitError = () => {
            fileLimitError.value = null
        }

        // The notice is only true while at capacity: drop it once room frees up.
        watch(isAttachmentLimitReached, (reached) => {
            if (!reached) {
                fileLimitError.value = null
            }
        })

        async function handleFileSelect(file: File) {
            if (isAttachmentLimitReached.value) {
                // Whichever cap is tighter is the one that blocked this file.
                fileLimitError.value =
                    maxFilesPerChat.value -
                        sentFileCount.value -
                        pendingAttachments.value.length <=
                    maxFilesPerMessage.value - pendingAttachments.value.length
                        ? { reason: 'per-chat', max: maxFilesPerChat.value }
                        : {
                              reason: 'per-message',
                              max: maxFilesPerMessage.value,
                          }
                return
            }
            const id = generateId()
            const localUrl = URL.createObjectURL(file)
            const attachment: PendingAttachment = {
                id,
                file,
                localUrl,
                state: 'uploading',
            }
            pendingAttachments.value = [...pendingAttachments.value, attachment]

            try {
                const mediaSource = await apiClient.value.uploadMedia(
                    agentIdRef.value,
                    localChatId.value,
                    file,
                )
                pendingAttachments.value = pendingAttachments.value.map((a) =>
                    a.id === id ? { ...a, state: 'done', mediaSource } : a,
                )
            } catch (err) {
                const message =
                    err instanceof Error ? err.message : 'Upload failed'
                pendingAttachments.value = pendingAttachments.value.map((a) =>
                    a.id === id ? { ...a, state: 'error', error: message } : a,
                )
            }
        }

        async function submitVote(
            message: UIChatMessage,
            vote: 'upvote' | 'downvote',
        ) {
            const { userQuestion, assistantMessage } =
                extractFeedbackContext(message)
            const feedback = await apiClient.value.submitMessageFeedback({
                messageId: message.id,
                chatId: localChatId.value,
                agentId: agentIdRef.value,
                vote,
                userQuestion,
                assistantMessage,
            })
            upsertLocalFeedback(feedback)
        }

        async function onUpvote(message: UIChatMessage) {
            await submitVote(message, 'upvote')
        }

        async function onDownvote(message: UIChatMessage) {
            await submitVote(message, 'downvote')
        }

        function onFeedback(message: UIChatMessage) {
            isFeedbackSubmitted.value = false
            feedbackSubmitError.value = undefined
            feedbackDialogMessage.value = message
        }

        async function onFeedbackSubmit(comment: string) {
            if (!feedbackDialogMessage.value) {
                return
            }
            try {
                isFeedbackSubmitting.value = true
                isFeedbackSubmitted.value = false
                feedbackSubmitError.value = undefined
                const { userQuestion, assistantMessage } =
                    extractFeedbackContext(feedbackDialogMessage.value)
                const feedback = await apiClient.value.submitMessageFeedback({
                    messageId: feedbackDialogMessage.value.id,
                    chatId: localChatId.value,
                    agentId: agentIdRef.value,
                    userQuestion,
                    assistantMessage,
                    comment,
                })
                upsertLocalFeedback(feedback)
                isFeedbackSubmitted.value = true
                await loadMessageFeedbacks(localChatId.value)
            } catch {
                feedbackSubmitError.value = te('message.feedbackSubmitError')
                    ? t('message.feedbackSubmitError')
                    : 'Failed to submit feedback'
            } finally {
                isFeedbackSubmitting.value = false
            }
        }

        async function onLeadSubmit(data: Record<string, unknown>) {
            try {
                isLoadingSubmitLead.value = true
                isLeadSubmitted.value = false
                submitLeadError.value = undefined
                await apiClient.value.submitLead({
                    agentId: agentIdRef.value,
                    chatId: localChatId.value,
                    ...data,
                })
                isLeadSubmitted.value = true
            } catch {
                submitLeadError.value = te('message.leadSubmitError')
                    ? t('message.leadSubmitError')
                    : 'Failed to submit lead'
            } finally {
                isLoadingSubmitLead.value = false
            }
        }

        async function renameChatTitle(chatId: string, title: string) {
            const updated = await apiClient.value.updateChatTitle(
                agentIdRef.value,
                chatId,
                title,
            )
            conversations.value = conversations.value.map((c) =>
                c.id === chatId ? { ...c, title: updated.title } : c,
            )
        }

        async function deleteChat(chatId: string) {
            await apiClient.value.deleteChat(agentIdRef.value, chatId)
            conversations.value = conversations.value.filter(
                (c) => c.id !== chatId,
            )
            if (localChatId.value === chatId) {
                await startNewChat()
            }
        }

        function navigate(view: ChatbotView) {
            activeView.value = view
        }

        // === Identity switches ===
        /**
         * Last userId the chat state was bound to. Starts undefined: adopting
         * the first identity of the page must not reset anything, because in
         * every same-user flow (anonymous re-auth, restored session) the
         * persisted chatId belongs to it, while `loadChat` leaves foreign
         * chats empty on a fresh page load anyway.
         */
        let boundUserId: string | undefined

        /**
         * GET the current chat as the new user, retrying once on failure: a
         * transient network error must not be mistaken for "not owned" and
         * wipe a conversation the user actually keeps. After the retry it
         * still fails closed (privacy wins over continuity).
         */
        function isChatReadable(chatId: string): Promise<boolean> {
            const probe = () =>
                apiClient.value
                    .getChat(agentIdRef.value, chatId)
                    .then((data) => data !== null)
            return probe().catch(() => probe().catch(() => false))
        }

        /**
         * The persisted chatId and the in-memory messages survive auth
         * changes, so when the session identity changes (host login via
         * external token, sign-out followed by a fresh anonymous user) the
         * previous user's conversation would keep showing. The server
         * enforces chat ownership, so ask it whether the current chat is
         * readable by the new user: it is only when it was transferred along
         * (in-widget sign-in links the anonymous user and moves their chats).
         * Otherwise drop the previous user's state and start a fresh chat.
         */
        watch(userId, async (newUserId) => {
            if (!newUserId || newUserId === boundUserId) {
                // Sign-out (undefined) keeps the state: it is either followed
                // by a new identity (anonymous fallback / next login), which
                // resets here, or no chat is rendered at all.
                return
            }
            const previousUserId = boundUserId
            boundUserId = newUserId
            if (previousUserId === undefined) {
                return
            }
            const verifiedChatId = localChatId.value
            // Reasoning chats are never persisted server-side, so the probe
            // could only fail: skip it and reset directly.
            const isChatOwnedByNewUser = isReasoningAgent.value
                ? false
                : await isChatReadable(verifiedChatId)
            if (
                userId.value !== newUserId ||
                localChatId.value !== verifiedChatId
            ) {
                // Superseded: another identity change or a chat rotation won
                return
            }
            if (isChatOwnedByNewUser) {
                // Same conversation, new owner (anonymous → linked account):
                // keep the messages, refresh the user-scoped list.
                loadConversations(agentIdRef.value)
                return
            }
            conversations.value = []
            messageFeedbacks.value = []
            chatFiles.value = []
            // Rotating the chatId recreates the Chat instance (clearing the
            // previous user's messages) and re-triggers `initialize` in the
            // components watching it, which reloads the user-scoped data.
            await startNewChat()
            input.value = ''
        })

        return {
            // Configuration
            apiClient,
            baseUrl,
            basePath,
            agentId: agentIdRef,
            name,
            agentType,
            userId,
            agentInterface,
            agentModel,
            agentTools,
            headers,
            revisedAnswers,
            model,
            actionsInput,
            actions,
            agentFileUpload,
            agentVoice,
            fallbackModels,
            gatewayOptions,
            externalContext,
            systemTheme,
            hideWelcomeMessage,
            isDark,
            // Chat state
            chat,
            messages,
            conversations,
            isLoadingConversations,
            chatFiles,
            isLoadingChatFiles,
            input,
            pendingAttachments,
            isAttachmentLimitReached,
            fileLimitError,
            localChatId,
            isConversationBlocked,
            inputMessagePlaceholder,
            chatError,
            // Navigation
            activeView,
            // Feedback state
            messageFeedbacks,
            feedbackDialogMessage,
            isFeedbackSubmitting,
            isFeedbackSubmitted,
            feedbackSubmitError,
            // Lead state
            isLeadSubmitted,
            isLoadingSubmitLead,
            submitLeadError,
            // Actions
            buildDeviceContext,
            initialize,
            loadConversations,
            loadChatFiles,
            refreshMessages,
            appendMessage,
            getChatFileUrl,
            getChatFilePreviewUrl,
            deleteChatFile,
            handleSubmit,
            sendMessage,
            stopGeneration,
            startNewChat,
            regenerate,
            addToolOutput,
            addToolApprovalResponse,
            handleFileSelect,
            clearFileLimitError,
            clearPendingAttachments,
            onUpvote,
            onDownvote,
            onFeedback,
            onFeedbackSubmit,
            onLeadSubmit,
            navigate,
            renameChatTitle,
            deleteChat,
        }
    })()
}
